import React from "react";

import Header from "../Header";
import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";
import { Progress } from "../Progress";
import { Text } from "../Text";

import styles from "./ProgressBar.scss";

/**
 * Props for the ProgressBar component.
 */
export interface ProgressBarProps {
  /**
   * Main text above bar
   * @default null
   */
  header?: JSX.Element | string;

  /**
   * Sub text below bar
   * @default null
   */
  helper?: string;

  /**
   * A decimal number [0, 1] on minimum Progress fill
   * @default 0
   */
  minPercent?: number;

  /**
   * A decimal number [0, 1] on maximum Progress fill
   * @default 1
   */
  maxPercent?: number;

  /**
   * A decimal number [0, 1] on how filled the Progress is
   */
  percent: number;

  /**
   * Icon type if present in the header
   */
  icon?: IconDictionary;

  /**
   * Header size. See confetti/Header.SIZES for values.
   * @default 'h3'
   */
  headerSize?: string;

  /**
   * CSS class of main wrapper
   * @default ''
   */
  className?: string;

  /**
   * Style attribute override
   */
  style?: Record<string, any>;

  /**
   * A test ID
   */
  testId?: string;

  /**
   * Whether helper text should be displayed above bar
   */
  helperTextOnTop?: boolean;
}

/**
 * A compound confetti component that is a linear Progress element decorated with header and helper text.
 * The header has a different styling scenario depending on whether an icon is passed into the component.
 */
export function ProgressBar({
  header,
  helper,
  helperTextOnTop,
  percent,
  icon,
  headerSize = "h3",
  className = "",
  style,
  testId,
}: ProgressBarProps): JSX.Element {
  // Use the Header utility if there is an icon, otherwise use the Label utility
  function renderHeader(): JSX.Element | null {
    if (!header) {
      return null;
    }

    if (icon) {
      return (
        <div className={styles["header_with_icon_wrapper"]}>
          <Icon icon={icon} />
          <Header
            level="3"
            size={headerSize}
            data-testid="progress-bar-header-with-icon"
            className={styles["header_with_icon"]}
          >
            {header}
          </Header>
        </div>
      );
    }

    return (
      <div className={styles.progressLabel} data-testid="progress-bar-header">
        <label>{header}</label>
      </div>
    );
  }

  return (
    <div className={className} style={style} data-testid={testId}>
      {renderHeader()}
      {helper && helperTextOnTop && (
        <Text
          size="timestamp"
          data-testid="progress-bar-helper"
          className={styles["helper-on-top"]}
        >
          {helper}
        </Text>
      )}
      <Progress percent={percent} />
      {helper && !helperTextOnTop && (
        <Text
          size="paragraphXsmall"
          data-testid="progress-bar-helper"
          className={styles.helper}
        >
          {helper}
        </Text>
      )}
    </div>
  );
}
